import { apiRequest } from './apiClient.js';
import type { SearchResult } from '../types/search.js';

export type { SearchResult } from '../types/search.js';

export type SearchResponse = {
  results: SearchResult[];
  totalResults?: number;
  searchTime?: number;
};

const cache = new Map<string, SearchResponse>();

/**
 * Fallback for the shortest query the server will accept (#384). Mirrors the
 * backend's `SEARCH_QUERY_MIN_LENGTH` DEFAULT — it is not authoritative.
 *
 * The server is the boundary and still answers 400 below its own configured
 * length; this threshold exists so the UI does not SEND such a request in the
 * first place. Without it, typing the second character of a search pops an error
 * banner — `useConversationController.describeSearchError` surfaces a 400 body
 * verbatim — where the honest state is "keep typing", not "something went wrong".
 *
 * This constant is only the value used BEFORE `GET /api/auth/status` has been
 * read. Hard-coding it outright was wrong in the RAISING direction: a deployment
 * setting `SEARCH_QUERY_MIN_LENGTH=5` would leave the client happily sending 3-
 * and 4-character queries the server 400s, reinstating exactly the mid-keystroke
 * error banner this guard removes. The effective minimum is therefore learned
 * from the server (`searchQueryMinLength` on `/api/auth/status`, the same
 * server-advertised-config carrier `chatAppUrl` uses) via
 * `setEffectiveSearchQueryMinLength`.
 */
export const SEARCH_QUERY_MIN_LENGTH = 3;

let effectiveSearchQueryMinLength = SEARCH_QUERY_MIN_LENGTH;

/**
 * Adopt the server's configured minimum. Called by `authService` whenever
 * `/api/auth/status` is read. Ignores anything that is not a whole number >= 1 so
 * a malformed/absent field leaves the default in place.
 */
export function setEffectiveSearchQueryMinLength(value: unknown): void {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1) {
    effectiveSearchQueryMinLength = value;
  }
}

/** The minimum currently in force: the server's if known, else the default. */
export function getEffectiveSearchQueryMinLength(): number {
  return effectiveSearchQueryMinLength;
}

/** Test-only: forget anything learned from the server. */
export function resetEffectiveSearchQueryMinLength(): void {
  effectiveSearchQueryMinLength = SEARCH_QUERY_MIN_LENGTH;
}

/**
 * Length of `query` in CHARACTERS (Unicode code points), matching the server's
 * measure in `validateSearchQuery`.
 *
 * `String.prototype.length` counts UTF-16 CODE UNITS, which is not the same
 * thing: `'𠮷野'` is 2 characters but 3 code units, so a plain `.length` check
 * would let a 2-character query through a 3-character minimum and straight onto
 * the unindexed ILIKE the minimum exists to prevent. Spreading iterates code
 * points, so both sides agree.
 */
export function searchQueryLength(query: string): number {
  return [...query.trim()].length;
}

/** True when `query` is long enough to be worth sending to `GET /api/search`. */
export function isSearchQueryLongEnough(query: string): boolean {
  return searchQueryLength(query) >= effectiveSearchQueryMinLength;
}

/**
 * Message for a query that is too short to send, so the UI can say "keep typing"
 * instead of silently doing nothing. Null when the query is long enough (or
 * empty, where the idle state is already the honest one).
 */
export function describeSearchQueryTooShort(query: string): string | null {
  const length = searchQueryLength(query);
  if (length === 0 || length >= effectiveSearchQueryMinLength) {
    return null;
  }
  const min = effectiveSearchQueryMinLength;
  return min === 1
    ? 'Type at least 1 character to search.'
    : `Type at least ${min} characters to search.`;
}

export function searchConversations(query: string): Promise<SearchResult[]> {
  const url = `/api/search?query=${encodeURIComponent(query)}`;
  return apiRequest<SearchResult[]>(url);
}

export async function search(query: string): Promise<SearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed || !isSearchQueryLongEnough(trimmed)) {
    return [];
  }
  const cached = cache.get(trimmed);
  if (cached) {
    return cached.results;
  }
  const results = await searchConversations(trimmed);
  cache.set(trimmed, { results });
  return results;
}

export async function searchWithPagination(
  query: string,
  _page = 1,
  _pageSize = 10
): Promise<SearchResponse> {
  const results = await search(query);
  return { results, totalResults: results.length };
}

export function clearCache(): void {
  cache.clear();
}

export function getCachedResults(query: string): SearchResult[] | null {
  const trimmed = query.trim();
  return cache.get(trimmed)?.results ?? null;
}

export async function isSearchAvailable(): Promise<boolean> {
  try {
    const status = await apiRequest<{ available?: boolean }>('/api/tools/status');
    return status.available !== false;
  } catch {
    return false;
  }
}
